import { exec, getApiToken } from '../utils';
import type { CatalogRule, SerializedCondition } from '@src/components/catalog/CatalogRules/types';
import { isFamilyField, isJsonField, serializeConditionItem, serializePairs } from '@src/components/catalog/CatalogRules/types';

interface CatalogRuleActionInput {
  field: string;
  value: string | Record<string, string | string[]>;
}

export interface CatalogRuleInput {
  id?: number;
  position: number;
  enabled: boolean;
  conditions: SerializedCondition[];
  actions: CatalogRuleActionInput[];
}

interface SaveCatalogRulesArgs {
  partnerCode: string;
  token: string;
  rules: CatalogRuleInput[];
}

interface SaveCatalogRulesResult {
  jobQueued: boolean;
}

interface SaveCatalogRulesExecResponse {
  restUser: {
    partner: {
      saveCatalogRules: SaveCatalogRulesResult;
    };
  };
}

export const toRuleInputs = (rules: CatalogRule[]): CatalogRuleInput[] =>
  rules.map((group, index) => ({
    ...(isFinite(Number(group.id)) ? { id: Number(group.id) } : {}),
    position: index,
    enabled: group.enabled,
    conditions: group.conditions.map(serializeConditionItem),
    actions: group.actions.map(a => ({
      field: a.field,
      value: isJsonField(a.field) && a.pairs.length > 0 ?
        serializePairs(a.pairs, isFamilyField(a.field)) :
        a.value,
    })),
  }));

/**
 * Saves catalog rules for a partner. Replaces the full set of rules.
 * @param partnerCode - The partner code to save rules for
 * @param rules - The complete array of rule groups to persist
 * @returns Promise resolving to save result with itemsUpdated count and saved rules
 */
export const saveCatalogRules = async (
  partnerCode: string,
  rules: CatalogRule[],
): Promise<SaveCatalogRulesResult> => {
  const apiToken = await getApiToken();

  const mutation = `
    mutation saveCatalogRules(
      $partnerCode: String!
      $token: String!
      $rules: [CatalogRuleInputRestUser!]!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          saveCatalogRules(rules: $rules) {
            jobQueued
          }
        }
      }
    }
  `;

  const data = await exec<SaveCatalogRulesArgs, SaveCatalogRulesExecResponse>(
    mutation,
    { variables: { token: apiToken, partnerCode, rules: toRuleInputs(rules) } },
  );

  return data.restUser.partner.saveCatalogRules;
};
